import React, { useState, useEffect, Suspense, lazy } from 'react';
import { withTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import { EmployerHero, CompaniesSlider } from './organisms';
import EmployerLayout from './EmployerLayout';

const EmployerAudience = lazy(() => import('./organisms/EmployerAudience'));
const EmployerWorkflow = lazy(() => import('./organisms/EmployerWorkflow'));
const EmployerTestimonial = lazy(() => import('./organisms/EmployerTestimonial'));
const EmployerPricing = lazy(() => import('./organisms/EmployerPricing'));
const EmployerFeaturesCTA = lazy(() => import('./organisms/EmployerFeaturesCTA'));
const EmployerSignupSection = lazy(() => import('./EmployerSignupSection'));
const EmployerCandidatesSection = lazy(() => import('./EmployerCandidatesSection'));

const LoadingFallback = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ color: '#8a99a8' }}>Loading...</div>
  </div>
);

const JobDetail = ({ jobId, onBack }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchJob() {
      try {
        const res = await fetch(`/api/v1/jobs/${jobId}`);
        if (!res.ok) throw new Error('Job not found');
        const data = await res.json();
        setJob(data.job || data);
      } catch (err) {
        console.error('Error loading job:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchJob();
  }, [jobId]);

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ minHeight: '100vh', background: '#0d1b2a', padding: '100px 5%', color: '#8a99a8' }}
      >
        Loading job details...
      </div>
    );
  }

  if (!job) {
    return (
      <div role="alert" style={{ minHeight: '100vh', background: '#0d1b2a', padding: '100px 5%', color: '#8a99a8' }}>
        <h1>Job not found</h1>
        <p>The job you are looking for may have been removed or is no longer available.</p>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#2dbd96',
            cursor: 'pointer',
            fontSize: '1rem',
            marginTop: '16px',
            padding: 0
          }}
        >
          ← Back to jobs
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0d1b2a', padding: '100px 5%' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <button
          onClick={onBack}
          aria-label="Back to jobs list"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#2dbd96',
            cursor: 'pointer',
            fontSize: '1rem',
            marginBottom: '40px',
            padding: 0
          }}
        >
          ← Back to jobs
        </button>

        <div style={{ display: 'flex', gap: '24px', marginBottom: '32px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '12px',
              background: job.company?.color || '#2dbd96',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0
            }}
          >
            {job.company?.logo_url || job.company?.logo ? (
              <img
                src={job.company.logo_url || job.company.logo}
                alt={job.company?.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <span style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 700 }}>
                {job.company?.name?.slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#2dbd96', fontSize: '0.875rem', fontWeight: 600, marginBottom: '8px' }}>
              {job.company?.name}
            </div>
            <h1
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                fontWeight: 900,
                color: '#ffffff',
                lineHeight: 1.2,
                marginBottom: '12px'
              }}
            >
              {job.position_title}
            </h1>
            <div style={{ display: 'flex', gap: '16px', color: '#8a99a8', fontSize: '0.9rem', flexWrap: 'wrap' }}>
              <span>{job.office?.city || job.office_city || 'Remote'}</span>
              <span>·</span>
              <span>{job.employment_type}</span>
              {job.deadline && (
                <>
                  <span>·</span>
                  <span>Deadline: {new Date(job.deadline).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <div
            style={{
              display: 'inline-block',
              padding: '6px 16px',
              background: 'rgba(45, 189, 150, 0.15)',
              border: '1px solid rgba(45, 189, 150, 0.3)',
              borderRadius: '100px',
              color: '#2dbd96',
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            {job.job_functions?.slice(0, 2).join(', ') || 'General'}
          </div>
        </div>

        {job.brief && (
          <div style={{ marginBottom: '40px' }}>
            <h3
              style={{
                color: '#ffffff',
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: '16px'
              }}
            >
              Job Brief
            </h3>
            <div
              style={{ color: '#c5cdd8', fontSize: '1rem', lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: job.brief }}
            />
          </div>
        )}

        {job.ideal_profile && (
          <div style={{ marginBottom: '40px' }}>
            <h3
              style={{
                color: '#ffffff',
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: '16px'
              }}
            >
              Ideal Candidate Profile
            </h3>
            <div
              style={{ color: '#c5cdd8', fontSize: '1rem', lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: job.ideal_profile }}
            />
          </div>
        )}

        <a
          href={`/jobs/${job.id}/applications/new`}
          style={{
            display: 'inline-block',
            background: '#2dbd96',
            color: '#0d1b2a',
            padding: '14px 32px',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 600,
            textDecoration: 'none',
            marginTop: '16px'
          }}
        >
          Apply Now
        </a>
      </div>
    </div>
  );
};

const ArticleDetail = ({ slug, onBack }) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchArticle() {
      try {
        const res = await fetch(`/api/v1/articles`);
        const data = await res.json();
        const match = data.articles?.data?.find(a => a.slug === slug);
        setArticle(match);
      } catch (err) {
        console.error('Error loading article:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ minHeight: '100vh', background: '#0d1b2a', padding: '100px 5%', color: '#8a99a8' }}
      >
        Loading article...
      </div>
    );
  }

  if (!article) {
    return (
      <div role="alert" style={{ minHeight: '100vh', background: '#0d1b2a', padding: '100px 5%', color: '#8a99a8' }}>
        <h1>Article not found</h1>
        <p>The article you are looking for may have been removed or is no longer available.</p>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#2dbd96',
            cursor: 'pointer',
            fontSize: '1rem',
            marginTop: '16px',
            padding: 0
          }}
        >
          ← Back to articles
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0d1b2a', padding: '100px 5%' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <button
          onClick={onBack}
          aria-label="Back to articles list"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#2dbd96',
            cursor: 'pointer',
            fontSize: '1rem',
            marginBottom: '40px',
            padding: 0
          }}
        >
          ← Back to articles
        </button>
        <img
          src={article.image}
          alt={article.title}
          style={{ width: '100%', borderRadius: '16px', marginBottom: '32px' }}
        />
        <div
          style={{
            color: '#2dbd96',
            fontSize: '0.875rem',
            fontWeight: 600,
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}
        >
          {article.category || 'Article'}
        </div>
        <h1
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 900,
            color: '#ffffff',
            lineHeight: 1.2,
            marginBottom: '24px'
          }}
        >
          {article.title}
        </h1>
        <div style={{ color: '#8a99a8', fontSize: '1rem', marginBottom: '40px' }}>
          By {article.subtitle || 'The Sainoo Team'} ·{' '}
          {article.published_at ? new Date(article.published_at).toLocaleDateString() : ''}
        </div>
        <div
          style={{ color: '#ffffff', fontSize: '1.125rem', lineHeight: 1.8 }}
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </div>
    </div>
  );
};

const EmployerOverview = ({ t }) => {
  const location = useLocation();
  const history = useHistory();
  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const articleSlug = queryParams.article as string | undefined;
  const jobId = queryParams.job as string | undefined;

  const handleBackToJobs = () => {
    history.push('/jobs');
  };

  const handleBackToArticles = () => {
    history.push('/articles');
  };

  if (jobId) {
    return (
      <EmployerLayout title="Job Details">
        <JobDetail jobId={jobId} onBack={handleBackToJobs} />
      </EmployerLayout>
    );
  }

  if (articleSlug) {
    return (
      <EmployerLayout title="Article">
        <ArticleDetail slug={articleSlug} onBack={handleBackToArticles} />
      </EmployerLayout>
    );
  }

  return (
    <EmployerLayout title="Sainoo - Calmon Partners">
      <EmployerHero />
      <CompaniesSlider />
      <Suspense fallback={<LoadingFallback />}>
        <EmployerAudience />
      </Suspense>
      <Suspense fallback={<LoadingFallback />}>
        <EmployerWorkflow />
      </Suspense>
      <Suspense fallback={<LoadingFallback />}>
        <EmployerCandidatesSection />
      </Suspense>
      <Suspense fallback={<LoadingFallback />}>
        <EmployerTestimonial />
      </Suspense>
      <Suspense fallback={<LoadingFallback />}>
        <EmployerFeaturesCTA />
      </Suspense>
      <Suspense fallback={<LoadingFallback />}>
        <EmployerPricing />
      </Suspense>
      <Suspense fallback={<LoadingFallback />}>
        <EmployerSignupSection t={t} />
      </Suspense>
    </EmployerLayout>
  );
};

export default withTranslation(['employerHome', 'translations'])(EmployerOverview);